import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";

type Lang = "ar" | "en";

interface LangCtx {
  lang: Lang;
  dir: "rtl" | "ltr";
  setLang: (l: Lang) => void;
  t: <K extends keyof typeof DICT.en>(key: K) => string;
  toggle: () => void;
}

const DICT = {
  en: {
    kids: "Kids",
    parent: "Parent",
    plan: "Plan",
    settings: "Settings",
    stories: "Stories",
    videos: "Videos",
    coloring: "Coloring",
    challenge: "Daily Challenge",
    challenges: "Challenges",
    rewards: "Rewards",
    badges: "Badges",
    points: "Points",
    streak: "Streak",
    streakDays: "day streak",
    chooseProfile: "Choose a profile",
    whosPlaying: "Who's playing?",
    addChild: "Add child",
    backToHome: "Back",
    todaysAdventures: "Today's adventures",
    nextBadge: "Next badge",
    earnPoints: "Earn",
    pts: "pts",
    completed: "Completed",
    start: "Start",
    parentDashboard: "Parent Dashboard",
    weeklyReport: "This week",
    screenTime: "Daily screen time",
    minutes: "minutes",
    language: "Language",
    signOut: "Sign out",
    spend: "Redeem",
    shop: "Reward Shop",
    notEnough: "Not enough points yet",
  },
  ar: {
    kids: "الأطفال",
    parent: "الوالد",
    plan: "الاشتراك",
    settings: "الإعدادات",
    stories: "قصص",
    videos: "فيديو",
    coloring: "تلوين",
    challenge: "تحدي اليوم",
    challenges: "التحديات",
    rewards: "المكافآت",
    badges: "الأوسمة",
    points: "النقاط",
    streak: "السلسلة",
    streakDays: "يوم متتالي",
    chooseProfile: "اختر ملفًا",
    whosPlaying: "من سيلعب؟",
    addChild: "إضافة طفل",
    backToHome: "رجوع",
    todaysAdventures: "مغامرات اليوم",
    nextBadge: "الوسام التالي",
    earnPoints: "اكسب",
    pts: "نقطة",
    completed: "مكتمل",
    start: "ابدأ",
    parentDashboard: "لوحة الوالدين",
    weeklyReport: "هذا الأسبوع",
    screenTime: "وقت الشاشة اليومي",
    minutes: "دقيقة",
    language: "اللغة",
    signOut: "تسجيل خروج",
    spend: "استبدال",
    shop: "متجر المكافآت",
    notEnough: "نقاط غير كافية",
  },
} as const;

const Ctx = createContext<LangCtx | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  // Always start with "en" on first render to match SSR output and avoid
  // hydration mismatches. The stored preference is applied in the effect below.
  const [lang, setLangState] = useState<Lang>("en");

  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = localStorage.getItem("jozori_lang");
    if (stored === "ar" || stored === "en") setLangState(stored);
  }, []);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
      document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    }
    if (typeof window !== "undefined") localStorage.setItem("jozori_lang", lang);
  }, [lang]);

  // Sync across tabs / other writers of the same key.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === "jozori_lang" && (e.newValue === "ar" || e.newValue === "en")) {
        setLangState(e.newValue);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    if (!user) return;
    supabase
      .from("profiles")
      .select("locale")
      .eq("id", user.id)
      .single()
      .then(({ data }) => {
        if (data?.locale === "ar" || data?.locale === "en") setLangState(data.locale);
      });
  }, [user]);

  const setLang = (l: Lang) => {
    setLangState(l);
    if (user) supabase.from("profiles").update({ locale: l }).eq("id", user.id);
  };

  const value: LangCtx = {
    lang,
    dir: lang === "ar" ? "rtl" : "ltr",
    setLang,
    toggle: () => setLang(lang === "ar" ? "en" : "ar"),
    t: (k) => (DICT[lang] as Record<string, string>)[k] ?? (DICT.en as Record<string, string>)[k],
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLang() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useLang must be used within LanguageProvider");
  return v;
}
